import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useSharePdf } from '@euler/functions/sharing/useSharePdf';
import {
  BiDashboardExportView,
  BiExportStatus,
  kBiExportFailureTexts,
  kBiExportPageSizeNames,
} from '@euler/model/bi/export';
import { kBiPaletteNames } from '@euler/model/bi/viz';
import dayjs from 'dayjs';
import { Download } from 'lucide-react-native';
import { memo } from 'react';
import { View } from 'react-native';
import { BiButton } from '../components/ui/BiButton';
import { BiSurfaceCard } from '../components/ui/BiSurfaceCard';
import { BiTag } from '../components/ui/BiTag';

const kStatus: Record<BiExportStatus, { text: string; color: string }> = {
  [BiExportStatus.Queued]: { text: '排队中', color: aiColors.mutedForeground },
  [BiExportStatus.Running]: { text: '导出中', color: aiColors.blue },
  [BiExportStatus.Succeeded]: { text: '已完成', color: aiColors.green },
  [BiExportStatus.Failed]: { text: '失败', color: aiColors.red },
};

function time(iso: string | null): string {
  return iso ? dayjs(iso).format('MM-DD HH:mm:ss') : '—';
}

function size(bytes: number | null): string | null {
  if (bytes === null) return null;
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * The file's name when saved: the dashboard and its dates. No spaces: the
 * native share sheet takes it as a `file://` path.
 */
function fileName(item: BiDashboardExportView): string {
  const dates = item.window ? `-${item.window.from}至${item.window.to}` : '';
  const name = item.dashboardName.replace(/[\\/:*?"<>|\s]+/g, '_');
  return `${name}${dates}.pdf`;
}

/**
 * One export: what it is (dashboard, dates, size, palette), where it
 * stands, its times, why it failed, and 下载 once its file is ready.
 */
export const ExportRow = memo(
  ({
    item,
    downloadUrl,
  }: {
    item: BiDashboardExportView;
    /** The link to open, read again when it may have expired. */
    downloadUrl: (item: BiDashboardExportView) => Promise<string | null>;
  }) => {
    const { rpx } = useResponsiveContext();
    const share = useSharePdf(fileName(item));
    const download = async () => {
      try {
        const url = await downloadUrl(item);
        if (url) await share(url);
      } catch (error) {
        console.error('BI export download failed', error);
      }
    };
    const status = kStatus[item.status];
    const facts = [
      item.window ? `${item.window.from} 至 ${item.window.to}` : null,
      kBiExportPageSizeNames[item.pageSize],
      `${kBiPaletteNames[item.palette]}配色`,
      item.pageCount ? `${item.pageCount} 页` : null,
      size(item.fileSize),
    ].filter(Boolean);
    return (
      <BiSurfaceCard>
        <View style={{ padding: rpx(14), gap: rpx(8) }}>
          <View
            style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(8) }}
          >
            <Typography.Text
              medium
              size={rpx(14)}
              color={aiColors.foreground}
              numberOfLines={1}
              style={{ flexShrink: 1 }}
            >
              {item.dashboardName}
            </Typography.Text>
            <BiTag text={status.text} color={status.color} />
          </View>
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            {facts.join(' · ')}
          </Typography.Text>
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            {`提交 ${time(item.queuedAt)} · 开始 ${time(
              item.startedAt,
            )} · 完成 ${time(item.finishedAt)}`}
          </Typography.Text>
          {item.failure ? (
            <Typography.Text size={rpx(12)} color={aiColors.red}>
              {kBiExportFailureTexts[item.failure]}
            </Typography.Text>
          ) : item.failedCardCount ? (
            <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
              {`${item.failedCardCount} 张卡片没有取到数据，PDF 中以提示代替。`}
            </Typography.Text>
          ) : null}
          {item.status === BiExportStatus.Succeeded ? (
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
              {item.downloadUrl ? (
                <BiButton
                  icon={Download}
                  title="下载"
                  tone="primary"
                  onPress={() => void download()}
                />
              ) : (
                <Typography.Text
                  size={rpx(12)}
                  color={aiColors.mutedForeground}
                >
                  文件暂不可下载
                </Typography.Text>
              )}
            </View>
          ) : null}
        </View>
      </BiSurfaceCard>
    );
  },
);
